/**
 * Per-stage provider assignments for local-chain custom orders.
 *
 * EU-path orders continue to use order_lines.assigned_provider_id (a single
 * FK on the line itself). LOCAL-path orders use rows in this table -- one per
 * stage (designer / printer / workshop). UI surfaces all three stages on
 * the order detail page.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@insolech/db';

export type LocalChainStage = 'designer' | 'printer' | 'workshop';

export const LOCAL_CHAIN_STAGES: readonly LocalChainStage[] = [
  'designer',
  'printer',
  'workshop',
];

export const LOCAL_CHAIN_STAGE_LABELS: Record<LocalChainStage, string> = {
  designer: 'Designer (shell CAD)',
  printer: 'Local printer (shell)',
  workshop: 'Workshop (finishing)',
};

export interface LineProviderAssignmentRow {
  id: string;
  order_line_id: string;
  stage: LocalChainStage;
  provider_id: string;
  assigned_at: string;
  assigned_by: string | null;
  provider: { id: string; name: string; contact_email: string | null } | null;
}

export const lineProviderAssignmentKeys = {
  all: ['line-provider-assignments'] as const,
  forLine: (lineId: string) =>
    [...lineProviderAssignmentKeys.all, 'line', lineId] as const,
};

export function lineProviderAssignmentsQuery(
  client: SupabaseClient<Database>,
  orderLineId: string,
) {
  return {
    queryKey: lineProviderAssignmentKeys.forLine(orderLineId),
    queryFn: async (): Promise<LineProviderAssignmentRow[]> => {
      const { data, error } = await client
        .from('line_provider_assignments')
        .select(
          'id, order_line_id, stage, provider_id, assigned_at, assigned_by, provider:providers(id, name, contact_email)',
        )
        .eq('order_line_id', orderLineId);
      if (error) throw error;
      return (data ?? []) as unknown as LineProviderAssignmentRow[];
    },
  };
}

/**
 * Upsert a stage assignment for a line. Pass providerId=null to clear the
 * stage. Idempotent — the unique (order_line_id, stage) constraint means
 * re-assigning replaces the existing row in one call.
 */
export async function setLineStageProvider(
  client: SupabaseClient<Database>,
  args: {
    orderLineId: string;
    stage: LocalChainStage;
    providerId: string | null;
    assignedBy: string;
  },
): Promise<void> {
  if (args.providerId === null) {
    const { error } = await client
      .from('line_provider_assignments')
      .delete()
      .eq('order_line_id', args.orderLineId)
      .eq('stage', args.stage);
    if (error) throw error;
    return;
  }

  // assigned_at default fires only on INSERT, so explicitly refresh it on
  // upsert; otherwise re-assigning a stage to a new provider preserves the
  // old timestamp and misleads any "assigned X days ago" indicator.
  const payload = {
    order_line_id: args.orderLineId,
    stage: args.stage,
    provider_id: args.providerId,
    assigned_by: args.assignedBy,
    assigned_at: new Date().toISOString(),
  };
  const { error } = await client
    .from('line_provider_assignments')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .upsert(payload as any, { onConflict: 'order_line_id,stage' });
  if (error) throw error;
}
